/**
 * Loader — a sheet of paper covers the page while the logo draws itself
 * and a counter runs to 100, then slides away as the opening arrives.
 * It plays whole once per visit; the next pages of the same tab only slide
 * the sheet away, the logo already drawn (ADR-0015). The sheet is the
 * server's (templates/site/base.html.twig).
 *
 * @module chrome/loader
 */

import { LOADER_SEEN, REDUCED_MOTION } from '../config.js';
import { ScrollTrigger, gsap } from '../lib/gsap.js';

/** The session key the head script of site/base reads before the first paint. */
const SEEN_KEY = 'dith-loader';

/**
 * @typedef {object} LoaderSteps
 * @property {() => void} onUnlock - The page is unlocked, still covered.
 * @property {() => void} onReveal - The sheet starts leaving: play the opening.
 * @property {() => void} onDone - The sheet is gone.
 */

/**
 * The next pages of this visit skip the long version. A storage the browser
 * refuses (strict private browsing) only means the loader plays whole again.
 */
function rememberPlayed() {
    try {
        sessionStorage.setItem(SEEN_KEY, '1');
    } catch {
        // Nothing to remember with: every page plays it whole, as before.
    }
}

/**
 * @param {LoaderSteps} steps
 */
export function playLoader({ onUnlock, onReveal, onDone }) {
    const loader = document.querySelector('[data-loader]');

    // Unlock the page while the sheet still covers it, so the reflow (and
    // the ScrollTrigger recalculation) happens out of sight.
    const unlock = () => {
        document.body.classList.remove('is-loading');
        ScrollTrigger.refresh();
        onUnlock();
    };

    const finish = () => {
        if (loader) {
            gsap.set(loader, { display: 'none' });
        }
        onDone();
    };

    if (!loader || REDUCED_MOTION) {
        unlock();
        onReveal();
        finish();
        return;
    }

    const mark = loader.querySelector('.loader__mark');

    // Already seen in this visit: the logo stands drawn (CSS), the sheet goes.
    if (LOADER_SEEN) {
        gsap.timeline({ onComplete: finish })
            .to(mark, { scale: 0.85, opacity: 0, duration: 0.3 })
            .call(unlock, undefined, '-=0.05')
            .to(loader, { yPercent: -100, duration: 0.7, ease: 'power3.inOut' })
            .call(onReveal, undefined, '<0.1');
        return;
    }

    rememberPlayed();

    const counter = loader.querySelector('[data-count]');
    const labels = [loader.querySelector('.loader__count'), loader.querySelector('.loader__word')];
    const progress = { value: 0 };

    gsap.timeline({ onComplete: finish })
        .set(mark.querySelectorAll('path'), { drawSVG: '0% 0%' })
        .to(mark.querySelectorAll('path'), { drawSVG: '0% 100%', duration: 1.1, stagger: 0.1 }, 0)
        .to(
            progress,
            {
                value: 100,
                duration: 1.3,
                ease: 'power2.inOut',
                onUpdate: () => {
                    counter.textContent = String(Math.round(progress.value));
                },
            },
            0,
        )
        .to(labels, { yPercent: 110, opacity: 0, duration: 0.5, stagger: 0.05 }, '-=0.2')
        .to(mark, { scale: 0.85, opacity: 0, duration: 0.5 }, '-=0.4')
        .call(unlock, undefined, '-=0.05')
        // The sheet leaves and the opening enters together — one move, not
        // two: the title rises into the space the sheet is vacating.
        .to(loader, { yPercent: -100, duration: 1, ease: 'power3.inOut' })
        .call(onReveal, undefined, '<0.15');
}
